/**
 * Orik inside the harness kernel: the entry that fills the harness's seats with Orik.
 *
 * The page is the harness's own web app — its frame, sidebar, conversation panel, right sidebar, Settings panel and
 * theme (`src/lib/agent/client-boot.ts`). This entry adds what is Orik's:
 *
 * - **Colours.** A token layer over the harness's theme, and this browser's theme choice (`orik-theme.ts`).
 * - **The shell's seats.** Brand, the account launcher, Orik's Settings pages and its dialogs (`orik-shell.ts`).
 * - **The conversation's seats.** Voice input, thread cost, the hero's mark and starters, and tool views
 *   (`orik-seats.ts`).
 * - **Delete.** dsh has no delete verb; Orik's route does one (`DELETE /api/sessions/:id`). It is offered as an
 *   entry of the conversation list's row menu, after `ui-workspace`'s own Pin, Rename, Fork and Archive.
 *
 * The settings namespace the harness's neighbours read is installed by `dsh-api-remotes` on every client; Orik's
 * host has no settings document, which the page answers for itself (`src/lib/agent/harness-transport.ts`).
 *
 * The module is a boot-graph row (`ORIK_HARNESS_ENTRY` in `src/lib/agent/client-boot.ts`) with no bundle:
 * {@link registerOrikHarness} hands its factory to the module loader before the kernel boots, so it is applied
 * during boot like any entry, and its occupants are registered before the frame first renders.
 */
import type { Context } from "@deepseek-ai/cordis";
import { createElement, type ComponentType, type ReactNode } from "react";
import { TrashIcon } from "lucide-react";
import { fileAddressFor } from "@deepseek-ai/dsh-util-workspace-path";
import { ORIK_HARNESS_ID } from "./entry-id";
import { installOrikSeats, type Primitives } from "./orik-seats";
import { installOrikShell } from "./orik-shell";
import { installOrikTheme, type ThemeService } from "./orik-theme";
import { setHarnessPrimitives, type HarnessPrimitives } from "./primitives";
import { useDeleteConversation } from "../src/lib/delete-conversation";

/** The slot registry's face, as far as Orik uses it. */
export interface Slots {
  register(options: Record<string, unknown>, component: ComponentType<never>): () => void;
  inject(key: string, effect: () => (() => void) | Iterable<() => void>): void;
}

/** The kernel context, as this module uses it. */
export type HarnessContext = Context & { slots: Slots; theme: ThemeService };

/** The kernel's service lookup, for services this entry reads only when a user acts. */
type Lookup = { get(name: string): unknown };

/**
 * Open one of a session's files in the right sidebar, as the conversation's own file links do (`ui-chat`).
 *
 * @param path - absolute, or relative to the session's working directory.
 */
function sessionFileOpener(ctx: HarnessContext): (sessionId: string, path: string) => void {
  return (sessionId, path) => {
    const lookup = ctx as unknown as Lookup;
    const sidebar = lookup.get("sidebarRight") as { openResource(address: string): void } | undefined;
    const sessions = lookup.get("sessions") as { list: { getSnapshot(): { byId: Record<string, { cwd?: string } | undefined> } } } | undefined;
    sidebar?.openResource(fileAddressFor(sessionId, sessions?.list.getSnapshot().byId[sessionId]?.cwd, path));
  };
}

/** The menu row the list's row-menu entries render: `ui-primitives`' own, from the module loader. */
type MenuItemButton = ComponentType<{ children: ReactNode; icon?: ReactNode; danger?: boolean; separatorBefore?: boolean; onSelect(): void }>;

/** What a row-menu entry receives: the row's identity and the menu's open state. */
interface SessionMenuItemProps {
  readonly sessionId: string;
  readonly displayTitle: string;
  useMenuOpenState(): readonly [boolean, (open: boolean) => void];
}

/** The Delete entry of a conversation's row menu: it closes the menu and asks for confirmation (`orik-shell.ts`). */
function deleteMenuItem(MenuItem: MenuItemButton): ComponentType<SessionMenuItemProps> {
  return function OrikDeleteSessionItem({ sessionId, displayTitle, useMenuOpenState }: SessionMenuItemProps): ReactNode {
    const [, setOpen] = useMenuOpenState();
    return createElement(MenuItem, {
      danger: true,
      separatorBefore: true,
      icon: createElement(TrashIcon, { "aria-hidden": true, className: "size-4" }),
      onSelect: () => {
        setOpen(false);
        useDeleteConversation.getState().request({ sessionId, title: displayTitle });
      },
      children: "Delete",
    });
  };
}

/** The kernel services this entry needs before it applies: the slot registry, the Remote root and the theme. */
const inject = ["slots", "remote", "theme"];

/**
 * The entry body.
 *
 * @param ctx - the kernel context this entry is applied to.
 * @param primitives - `ui-primitives`, for Orik's occupants.
 */
function apply(ctx: HarnessContext, primitives: HarnessPrimitives): void {
  // React's development build profiles every re-render by walking the changed props and reading `$$typeof`
  // on each object it meets. A harness component's props carry Cordis contexts (a session binding's scope),
  // and a context refuses any property it was not given — so a conversation opening under a mounted panel
  // threw from React's commit, and the panel never left "Loading history…". `$$typeof` is never a service:
  // answer it as absent ("not a React element") and let every other property resolve as before.
  ctx.on("internal/get", (_scope, name, _error, next) => name === "$$typeof" ? undefined : next());
  setHarnessPrimitives(primitives);
  installOrikTheme(ctx as unknown as Parameters<typeof installOrikTheme>[0]);
  installOrikShell(ctx);
  // After `ui-workspace`'s Archive (400), as the start of a group of its own.
  ctx.slots.inject("sidebar.workspaces.session.menu.item", () => ctx.slots.register({
    name: "sidebar.workspaces.session.menu.item",
    id: "orik-delete",
    order: 500,
  }, deleteMenuItem(primitives.MenuItemButton as MenuItemButton) as ComponentType<never>));
  installOrikSeats(ctx, primitives as unknown as Primitives, sessionFileOpener(ctx));
}

/** The module loader's bootstrap facade, as the injection table installs it. */
interface ModuleLoaderFacade {
  load(registration: { id: string; factory: (require: (id: string) => unknown) => unknown }): void;
}

/**
 * Register the entry's factory with the module loader, before the kernel boots.
 *
 * Called between the module system's registration and the kernel's start (`harness/in-app.ts`). The loader
 * queues registrations until the kernel creates it, and never fetches a row whose factory it holds. The factory
 * resolves `ui-primitives` through the loader, as every client plugin does, so Orik's occupants are drawn with
 * the same components, with the same styles, as the harness's own beside them.
 */
export function registerOrikHarness(): void {
  const loader = (globalThis as { __ModuleLoader__?: ModuleLoaderFacade }).__ModuleLoader__;
  if (loader === undefined) throw new Error("orik harness: the module loader is not installed yet");
  loader.load({
    id: ORIK_HARNESS_ID,
    factory: require => {
      const primitives = require("@deepseek-ai/dsh-client-ui-primitives") as HarnessPrimitives;
      return { inject, apply: (ctx: HarnessContext) => apply(ctx, primitives) };
    },
  });
}
